import type { AccessStatusResponse } from "../services/api/access";

/** Firebase가 Email/Password identity에 쓰는 provider id. */
const PASSWORD_PROVIDER_ID = "password";

/**
 * 승인 대기 화면을 소셜 계정 형태(Company 행 없음, label `Email`)로 그릴지 판정한다.
 *
 * 정본은 `/api/access/status`의 `request.authType`이다. 요청이 아직 없거나
 * authType을 못 읽은 경우에만 Firebase provider를 보조 근거로 쓴다. 둘 다
 * 모르면 Company 행을 남기는 쪽으로 판정해, 회사 계정에서 정보가 사라지는
 * 방향의 오작동을 만들지 않는다. STUDIO-AUTH-003 §6.
 */
export const isSocialAccessRequest = (
  status: AccessStatusResponse | null | undefined,
  providerIds: readonly string[] = [],
): boolean => {
  const authType = status?.request?.authType;
  if (authType === "social") return true;
  if (authType === "company") return false;

  return providerIds.some((id) => Boolean(id) && id !== PASSWORD_PROVIDER_ID);
};

export interface SocialProviderIcon {
  src: string;
  /** provider 이름. 이메일만으로는 어떤 계정으로 들어왔는지 알 수 없다. */
  alt: string;
}

/**
 * 승인 대기 화면 Email 행 앞에 붙는 provider 아이콘.
 *
 * 로그인 화면이 쓰는 자산을 그대로 쓴다. 배경이 어두우므로 Apple은 흰색
 * light 자산이어야 보인다. Figma `SocialLogin`(node 1997:99228) 기준.
 */
const GOOGLE_ICON: SocialProviderIcon = {
  src: "/Images/Icons/Google.png",
  alt: "Google",
};
const MICROSOFT_ICON: SocialProviderIcon = {
  src: "/Images/Icons/Microsoft.png",
  alt: "Microsoft",
};
const APPLE_ICON: SocialProviderIcon = {
  src: "/Images/Icons/Social Login_apple_light.png",
  alt: "Apple",
};

/**
 * provider 어휘가 출처마다 다르므로 둘 다 받는다.
 *
 * - Main API `request.provider`: 표시 label이다. `_provider_label`이 Firebase id를
 *   `Google`·`Microsoft`·`Apple`·`Email`로 바꿔서 내려준다.
 * - Firebase `providerData[].providerId`: `google.com` 형태의 raw id다.
 *
 * 한쪽 어휘만 알면 다른 출처에서 온 값이 조용히 아이콘 없음으로 떨어진다.
 */
const PROVIDER_ICONS: Readonly<Record<string, SocialProviderIcon>> = {
  google: GOOGLE_ICON,
  "google.com": GOOGLE_ICON,
  microsoft: MICROSOFT_ICON,
  "microsoft.com": MICROSOFT_ICON,
  apple: APPLE_ICON,
  "apple.com": APPLE_ICON,
};

/**
 * provider 문자열 하나로 아이콘을 찾는다. 모르는 값이면 null이다.
 * Email/Password(`Email`·`password`)는 소셜이 아니므로 당연히 null이다.
 */
export const getSocialProviderIcon = (
  provider: string | null | undefined,
): SocialProviderIcon | null =>
  PROVIDER_ICONS[(provider ?? "").trim().toLowerCase()] ?? null;

/**
 * 여러 출처를 순서대로 보고 처음 찾은 아이콘을 쓴다.
 *
 * API가 내려준 값이 알 수 없는 문자열이어도 Firebase provider로 이어서 찾는다.
 * 앞 값이 null이 아니라 "모르는 값"일 때 멈춰버리면 아이콘이 사라진다.
 */
export const resolveSocialProviderIcon = (
  candidates: readonly (string | null | undefined)[],
): SocialProviderIcon | null => {
  for (const candidate of candidates) {
    const icon = getSocialProviderIcon(candidate);
    if (icon) return icon;
  }
  return null;
};
